import { useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  isNativeAppShell,
  getNativePlatform,
  isNativeTabletShell,
  isTabletFormFactor,
  syncNativeTabletShellClass,
  MOBILE_LAYOUT,
} from '../utils/mobileView';

/**
 * Unified mobile context for responsive web + Capacitor native shells.
 *
 * - isMobile: narrow viewport (MUI md breakpoint)
 * - isNative: iOS/Android Capacitor app (phone or tablet)
 * - isTabletNative: iPad / large Android tablet form factor inside native app
 * - isTabletLayout: tablet/iPad form factor at md+ (web Safari iPad, etc.)
 * - isCompact: mobile app shell (bottom nav + mobile header).
 *   Always true for ANY native shell so iPad/Android tablets never get desktop chrome.
 *   On web: true only below the md breakpoint.
 */
export default function useMobileView() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isNative = isNativeAppShell();
  const platform = getNativePlatform();
  const isTabletNative = isNative && isNativeTabletShell();
  // Web tablet/iPad only — native tablets use the app shell via isCompact.
  const isTabletLayout = !isNative && isMdUp && isTabletFormFactor();
  const isCompact = isNative || isMobile;

  useEffect(() => {
    if (!isNative) {
      return undefined;
    }
    syncNativeTabletShellClass();
    const onResize = () => syncNativeTabletShellClass();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [isNative]);

  return {
    isMobile,
    isNative,
    isTabletNative,
    isTabletLayout,
    isCompact,
    platform,
    layout: MOBILE_LAYOUT,
  };
}
